import React, { useEffect, useMemo, useState } from 'react';
import {
  Wallet, Search, Loader2, Pause, Play, Eye, ShieldAlert, ShieldCheck,
  AlertCircle, CheckCircle2, XCircle, CalendarClock,
} from 'lucide-react';
import { api } from '../../App';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const fmtDate = (iso) => {
  if (!iso) return ',';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ','; }
};

const fmtDateTime = (iso) => {
  if (!iso) return ',';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ','; }
};

function StripeStatusPills({ status }) {
  if (!status) {
    return <span className="text-xs text-stone-400 italic">Not fetched</span>;
  }
  if (!status.connected) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-100 text-stone-500">
        <AlertCircle className="h-3 w-3" /> Not connected
      </span>
    );
  }
  const items = [
    { key: 'charges', label: 'Charges', ok: status.charges_enabled },
    { key: 'payouts', label: 'Payouts', ok: status.payouts_enabled },
    { key: 'onboarded', label: 'Onboarded', ok: status.onboarding_complete },
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((i) => (
        <span
          key={i.key}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
            i.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}
          title={`${i.label}: ${i.ok ? 'enabled' : 'disabled'}`}
          data-testid={`stripe-status-${i.key}`}
        >
          {i.ok ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
          {i.label}
        </span>
      ))}
    </div>
  );
}

function PauseSwitch({ row, onToggle, busy }) {
  const isPaused = row.payouts_paused;
  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={isPaused}
        disabled={busy}
        onCheckedChange={(checked) => onToggle(row, checked)}
        data-testid={`pause-switch-${row.provider_id}`}
        aria-label={isPaused ? 'Resume payouts' : 'Pause payouts'}
      />
      <span className={`text-xs font-medium ${isPaused ? 'text-amber-600' : 'text-stone-400'}`}>
        {isPaused ? 'Paused' : 'Active'}
      </span>
    </div>
  );
}

function PayoutDetailsDialog({ providerId, open, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!providerId || !open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/providers/${providerId}/payout-details`);
        if (!cancelled) setDetails(res.data);
      } catch {
        toast.error('Could not load payout details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [providerId, open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="payout-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Wallet className="h-5 w-5 text-[#D4AF37]" />
            Payout Details
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
          </div>
        )}

        {details && !loading && (
          <div className="space-y-5 mt-2">
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide">Provider</p>
              <p className="font-medium text-[#1A1A1A]">{details.brand_name}</p>
              <p className="text-xs text-stone-500">{details.user_email}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border border-[#E6E4DD] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-stone-400">Available</p>
                <p className="text-base font-semibold text-emerald-600">£{details.available_balance.toFixed(2)}</p>
              </div>
              <div className="rounded-lg border border-[#E6E4DD] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-stone-400">Pending</p>
                <p className="text-base font-semibold text-amber-500">£{details.pending_balance.toFixed(2)}</p>
              </div>
              <div className="rounded-lg border border-[#E6E4DD] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-stone-400">At risk</p>
                <p className="text-base font-semibold text-red-500">£{details.at_risk_balance.toFixed(2)}</p>
              </div>
              <div className="rounded-lg border border-[#E6E4DD] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-stone-400">Paid out</p>
                <p className="text-base font-semibold text-stone-700">£{details.already_paid_out.toFixed(2)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-[#F9F6F0] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-stone-400">Next scheduled</p>
                <p className="text-sm font-medium text-[#1A1A1A] inline-flex items-center gap-1">
                  <CalendarClock className="h-3.5 w-3.5 text-[#D4AF37]" />
                  {fmtDateTime(details.next_payout_date)}
                </p>
              </div>
              <div className="rounded-lg bg-[#F9F6F0] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-stone-400">Status</p>
                <p className={`text-sm font-medium ${details.payouts_paused ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {details.payouts_paused ? 'Paused' : 'Active'}
                </p>
                {details.payouts_paused && details.payouts_paused_reason && (
                  <p className="text-xs text-stone-500 mt-0.5">Reason: {details.payouts_paused_reason}</p>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Stripe Connect</p>
              <div className="rounded-lg border border-[#E6E4DD] p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-stone-500">Account ID</span>
                  <span className="font-mono text-xs text-stone-700">
                    {details.stripe_account_id || 'Not connected'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-stone-500">Status</span>
                  <StripeStatusPills status={details.stripe_status} />
                </div>
                {details.stripe_status?.requirements_due?.length > 0 && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                    <p className="font-medium text-amber-700 flex items-center gap-1">
                      <ShieldAlert className="h-3.5 w-3.5" /> Outstanding Stripe requirements
                    </p>
                    <p className="text-amber-600 mt-1">{details.stripe_status.requirements_due.join(', ')}</p>
                  </div>
                )}
                {details.stripe_status?.error === 'stripe_api_unavailable' && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Could not reach Stripe to verify live status
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">
                Payout history ({details.payouts.length})
              </p>
              {details.payouts.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-6 bg-[#F9F6F0]/60 rounded-lg">
                  No payouts yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E6E4DD] text-left">
                        <th className="py-2 px-2 text-stone-500 font-medium text-xs">Date</th>
                        <th className="py-2 px-2 text-stone-500 font-medium text-xs">Reference</th>
                        <th className="py-2 px-2 text-stone-500 font-medium text-xs text-right">Amount</th>
                        <th className="py-2 px-2 text-stone-500 font-medium text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.payouts.map((p) => (
                        <tr key={p.payout_id} className="border-b border-[#E6E4DD]" data-testid={`detail-payout-${p.payout_id}`}>
                          <td className="py-2 px-2 text-stone-600">{fmtDate(p.created_at)}</td>
                          <td className="py-2 px-2 font-mono text-xs text-stone-600">{p.reference || p.payout_id}</td>
                          <td className="py-2 px-2 text-right font-medium text-[#1A1A1A]">£{Number(p.amount || 0).toFixed(2)}</td>
                          <td className="py-2 px-2">
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                              p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              p.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                              p.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminPayoutsTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeStripe, setIncludeStripe] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | above_threshold | paused | not_connected
  const [busyRowId, setBusyRowId] = useState(null);
  const [detailsProviderId, setDetailsProviderId] = useState(null);

  const fetchOverview = async (stripe = false) => {
    if (stripe) setStripeLoading(true);
    else setLoading(true);
    try {
      const res = await api.get('/admin/payouts/overview', { params: stripe ? { include_stripe: true } : {} });
      setOverview(res.data);
    } catch {
      toast.error('Could not load payout overview');
    } finally {
      if (stripe) setStripeLoading(false);
      else setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(false); }, []);

  const handleTogglePause = async (row, shouldPause) => {
    const verb = shouldPause ? 'pause' : 'resume';
    let reason = null;
    if (shouldPause) {
      reason = window.prompt(`Pause payouts for ${row.brand_name}?\n\nOptional reason (visible to platform admins only):`, '');
      if (reason === null) return; // cancelled
      reason = reason.trim();
    } else if (!window.confirm(`Resume scheduled payouts for ${row.brand_name}?`)) {
      return;
    }

    setBusyRowId(row.provider_id);
    try {
      const params = { paused: shouldPause };
      if (shouldPause && reason) params.reason = reason;
      const res = await api.put(`/admin/providers/${row.provider_id}/payouts-pause`, null, { params });
      setOverview((prev) => prev && {
        ...prev,
        providers: prev.providers.map((p) => p.provider_id === row.provider_id ? {
          ...p,
          payouts_paused: res.data.payouts_paused,
          payouts_paused_at: res.data.payouts_paused_at,
          payouts_paused_reason: res.data.payouts_paused_reason,
        } : p),
      });
      toast.success(`Payouts ${verb}d for ${row.brand_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb} payouts`);
    } finally {
      setBusyRowId(null);
    }
  };

  const { rows, totals } = useMemo(() => {
    const all = overview?.providers || [];
    const q = search.trim().toLowerCase();
    let filtered = all;
    if (q) {
      filtered = filtered.filter((r) =>
        (r.brand_name || '').toLowerCase().includes(q) ||
        (r.user_email || '').toLowerCase().includes(q) ||
        (r.provider_id || '').toLowerCase().includes(q),
      );
    }
    if (filter === 'above_threshold') filtered = filtered.filter((r) => r.meets_threshold);
    else if (filter === 'paused') filtered = filtered.filter((r) => r.payouts_paused);
    else if (filter === 'not_connected') filtered = filtered.filter((r) => !r.stripe_account_id);

    const totalAvailable = all.reduce((s, r) => s + (r.available_balance || 0), 0);
    const totalPending = all.reduce((s, r) => s + (r.pending_balance || 0), 0);
    const pausedCount = all.filter((r) => r.payouts_paused).length;
    const eligibleCount = all.filter((r) => r.meets_threshold && !r.payouts_paused).length;

    return {
      rows: filtered,
      totals: { totalAvailable, totalPending, pausedCount, eligibleCount, all: all.length },
    };
  }, [overview, search, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!overview) {
    return <div className="card-surface p-8 text-center text-stone-500">Unable to load payout overview.</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-payouts-tab-content">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card-surface p-4">
          <p className="text-xs uppercase tracking-wide text-stone-400">Providers</p>
          <p className="text-xl font-semibold text-[#1A1A1A]" data-testid="admin-payout-total-providers">{totals.all}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-xs uppercase tracking-wide text-stone-400">Total Available</p>
          <p className="text-xl font-semibold text-emerald-600">£{totals.totalAvailable.toFixed(2)}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-xs uppercase tracking-wide text-stone-400">Total Pending</p>
          <p className="text-xl font-semibold text-amber-500">£{totals.totalPending.toFixed(2)}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-xs uppercase tracking-wide text-stone-400">Eligible Next Sweep</p>
          <p className="text-xl font-semibold text-[#D4AF37]" data-testid="admin-payout-eligible-count">{totals.eligibleCount}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-xs uppercase tracking-wide text-stone-400">Paused</p>
          <p className="text-xl font-semibold text-red-500" data-testid="admin-payout-paused-count">{totals.pausedCount}</p>
        </div>
      </div>

      {/* Next payout banner */}
      <div className="rounded-lg bg-[#F9F6F0] border border-[#E6E4DD] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-stone-600">
          <CalendarClock className="h-4 w-4 text-[#D4AF37]" />
          <span>Next scheduled sweep: <span className="font-medium text-[#1A1A1A]">{fmtDateTime(overview.next_payout_date)}</span></span>
          <span className="text-stone-400">· Minimum £{overview.payout_threshold.toFixed(0)} · Automatic</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchOverview(true)}
          disabled={stripeLoading}
          className="gap-1.5 border-[#E6E4DD]"
          data-testid="refresh-stripe-status-btn"
        >
          {stripeLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          {includeStripe || overview.providers.some((p) => p.stripe_status !== null) ? 'Refresh Stripe status' : 'Fetch Stripe status'}
        </Button>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            placeholder="Search by provider name, email, or id..."
            className="pl-9 border-[#E6E4DD]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-payout-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { v: 'all', label: 'All' },
            { v: 'above_threshold', label: 'Eligible' },
            { v: 'paused', label: 'Paused' },
            { v: 'not_connected', label: 'No Stripe' },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                filter === f.v
                  ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                  : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-stone-400'
              }`}
              data-testid={`admin-payout-filter-${f.v}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9F6F0]">
              <tr>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-xs">Provider</th>
                <th className="text-right py-3 px-4 text-stone-600 font-medium text-xs">Available</th>
                <th className="text-right py-3 px-4 text-stone-600 font-medium text-xs">Pending</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-xs">Last Payout</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-xs">Next Payout</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-xs">Stripe</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-xs">Payouts</th>
                <th className="text-right py-3 px-4 text-stone-600 font-medium text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-stone-400">No providers match this filter.</td></tr>
              ) : rows.map((row) => (
                <tr
                  key={row.provider_id}
                  className={`border-b border-[#E6E4DD] hover:bg-[#F9F6F0] ${row.payouts_paused ? 'bg-amber-50/30' : ''}`}
                  data-testid={`admin-payout-row-${row.provider_id}`}
                >
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-[#1A1A1A]">{row.brand_name}</p>
                    <p className="text-xs text-stone-400">{row.user_email || row.provider_id}</p>
                    {row.subscription_tier === 'pro' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] font-medium mt-0.5 inline-block">PRO</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`text-sm font-semibold ${row.meets_threshold ? 'text-emerald-600' : 'text-stone-700'}`}>
                      £{row.available_balance.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-amber-600">
                    £{row.pending_balance.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-xs text-stone-500">
                    {row.last_payout_date ? (
                      <>
                        <p className="text-stone-700">{fmtDate(row.last_payout_date)}</p>
                        <p className="text-stone-400">£{(row.last_payout_amount || 0).toFixed(2)} · {row.last_payout_status}</p>
                      </>
                    ) : (
                      <span className="text-stone-400 italic">Never</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-stone-500">{fmtDate(row.next_payout_date)}</td>
                  <td className="py-3 px-4"><StripeStatusPills status={row.stripe_status} /></td>
                  <td className="py-3 px-4">
                    <PauseSwitch row={row} onToggle={handleTogglePause} busy={busyRowId === row.provider_id} />
                    {row.payouts_paused && row.payouts_paused_reason && (
                      <p className="text-[10px] text-amber-600 mt-1 max-w-[160px] truncate" title={row.payouts_paused_reason}>
                        {row.payouts_paused_reason}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDetailsProviderId(row.provider_id)}
                      className="gap-1 text-[#1A1A1A] hover:text-[#D4AF37]"
                      data-testid={`view-payout-details-${row.provider_id}`}
                    >
                      <Eye className="h-3.5 w-3.5" /> Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-stone-400 text-center">
        Payouts run automatically every Monday at 04:00 UTC. Earnings clear 48 hours after service completion. Paused providers are skipped without affecting their balance.
      </p>

      <PayoutDetailsDialog
        providerId={detailsProviderId}
        open={!!detailsProviderId}
        onClose={() => setDetailsProviderId(null)}
      />
    </div>
  );
}
